import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { Input, Button, message, Typography, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "../config/apiConfig";
import { useTranslation } from "react-i18next";
import HomeButton from "../components/HomeButton";

const { TextArea } = Input;
const { Title } = Typography;

const DocumentQA: React.FC = () => {
  const { t } = useTranslation();
  const [fileId, setFileId] = useState<string | null>(null);
  const [answer, setAnswer] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const handleFileUpload = async (file: File) => {
    setLoading(true);
    setFileId(null);
    setAnswer("");
    setUploadedFileName(null);
    const token = localStorage.getItem("token");

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API_BASE_URL}/zhadiger/api/files`, {
        method: "POST",
        headers: {
          Authorization: `${token}`,
          ngrok: "skip-browser-warning",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to upload file");
      }

      const data = await response.json();
      setFileId(data.id);
      setUploadedFileName(file.name);
      message.success("File uploaded successfully!");
    } catch (error) {
      console.error("File upload error:", error);
      message.error("Error uploading file");
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionSubmit = async (values: { question: string }) => {
    if (!fileId) {
      message.error("Please upload a file first");
      return;
    }

    setLoading(true);
    setAnswer("");
    const token = localStorage.getItem("token");

    try {
      const requestBody = {
        userRequest: values.question,
        chatId: "",
        fileIdList: [fileId],
      };

      const response = await fetch(
        `${API_BASE_URL}/zhadiger/api/llm/ocr/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get answer");
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        lines.forEach((line) => {
          if (line.startsWith("data:")) {
            const data = line.slice(5);
            setAnswer((prev) => prev + data);
          }
        });
      }

      message.success("Answer retrieved successfully!");
    } catch (error) {
      console.error("Question submission error:", error);
      message.error("Error retrieving answer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4 ml-4 pt-4 ">
        <HomeButton />
      </div>
      <div className="max-w-xl mx-auto mt-10 p-6 bg-white border border-gray-200 rounded-lg shadow-md flex-grow">
        <Title level={2} className="text-center font-bold text-gray-700">
          {t("document_qa") || "Document Q&A"}
        </Title>

        <Upload
          beforeUpload={(file) => {
            handleFileUpload(file);
            return false;
          }}
          showUploadList={false}
          disabled={loading}
        >
          <Button
            icon={<UploadOutlined />}
            className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-lg py-2 transition duration-200"
            loading={loading}
          >
            {t("upload_file") || "Upload File"}
          </Button>
        </Upload>

        {uploadedFileName && (
          <div className="mt-4 text-center text-gray-600">
            {t("uploaded_file") || "Uploaded File"}:{" "}
            <strong>{uploadedFileName}</strong>
          </div>
        )}

        {
          <Formik
            initialValues={{ question: "" }}
            onSubmit={handleQuestionSubmit}
          >
            {({ handleSubmit }) => (
              <Form className="space-y-4 mt-6">
                <Field name="question">
                  {({ field }: { field: any }) => (
                    <TextArea
                      {...field}
                      placeholder={
                        t("ask_question") || "Ask a question about the document"
                      }
                      rows={4}
                      disabled={loading}
                      className="p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </Field>

                <Button
                  type="primary"
                  onClick={() => handleSubmit()}
                  loading={loading}
                  className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-lg py-2 transition duration-200"
                >
                  {t("get_answer") || "Get Answer"}
                </Button>
              </Form>
            )}
          </Formik>
        }

        {answer && (
          <div className="mt-6 text-center">
            <Title level={4} className="text-gray-600">
              {t("answer") || "Answer"}
            </Title>
            <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg whitespace-pre-wrap text-left">
              {answer}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentQA;
